import { useEffect, useState } from "react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { analyticsOptedOut, globalPrivacyControlOn, registerQaVisitor, setAnalyticsOptOut } from "@/lib/firstPartyAnalytics";
import { applyHeadMeta } from "@/lib/headMeta";

const Privacy = () => {
  const { isAdmin } = useAuth();
  const [gpc] = useState(globalPrivacyControlOn());
  const [optedOut, setOptedOut] = useState(analyticsOptedOut());

  useEffect(() => applyHeadMeta({
    title: "Privacy & Analytics | Say It In Sports",
    description: "How Say It In Sports measures site use with first-party analytics, and how to opt out.",
    url: "https://sayitinsports.app/privacy",
  }), []);

  const toggle = (allow: boolean) => {
    setAnalyticsOptOut(!allow);
    setOptedOut(analyticsOptedOut());
    toast.success(allow ? "Analytics turned on for this browser." : "Analytics turned off for this browser.");
  };

  const markQa = async () => {
    const { data, error } = await registerQaVisitor(24);
    if (error) return toast.error("Only verified admins can mark QA traffic.");
    toast.success(`This browser is marked as QA traffic until ${new Date(data as string).toLocaleString()}.`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container max-w-2xl py-12 space-y-8">
        <header>
          <h1 className="font-display text-4xl uppercase">Privacy &amp; Analytics</h1>
          <p className="mt-3 text-muted-foreground">How Say It In Sports measures how athlete pages are used.</p>
        </header>

        <section className="space-y-3 leading-relaxed">
          <h2 className="font-display text-2xl uppercase">What we measure</h2>
          <p>
            We use our own first-party analytics to understand how fans find and use athlete pages—for example,
            profile views, Follow and support button use, and which campaign or QR code brought a visit. This helps
            athletes and SIIS see what is working.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Data is stored only by Say It In Sports. It is not sold, shared with advertisers, or used to track you on other sites.</li>
            <li>We do not record your name, email address, IP address or browser details with these events.</li>
            <li>Your browser is given a random identifier, kept in your browser's storage (not a cookie). It is not linked to your account.</li>
            <li>We record the page, the website that referred you (domain only), campaign tags in the link, device type (phone, tablet or desktop) and whether you were signed in.</li>
            <li>Detailed event records are deleted after 13 months.</li>
          </ul>
          <p>
            Your follows, inquiries and support payments are kept as normal account and transaction records, separate
            from analytics, and opting out does not affect them.
          </p>
        </section>

        <section className="rounded-2xl border border-border p-6 space-y-4" aria-labelledby="analytics-choice">
          <h2 id="analytics-choice" className="font-display text-2xl uppercase">Your choice</h2>
          {gpc ? (
            <p role="status">
              Your browser is sending a Global Privacy Control signal, so analytics are off for this browser.
            </p>
          ) : (
            <label className="flex items-center justify-between gap-4">
              <span>Allow SIIS analytics in this browser</span>
              <Switch checked={!optedOut} onCheckedChange={toggle} aria-label="Allow SIIS analytics in this browser" />
            </label>
          )}
          <p className="text-sm text-muted-foreground">
            Turning analytics off only stops measurement. Following athletes, My Corner, support and inquiries keep working.
          </p>
        </section>

        {isAdmin && (
          <section className="rounded-2xl border border-dashed border-border p-6 space-y-3">
            <h2 className="font-display text-xl uppercase">Admin: QA traffic</h2>
            <p className="text-sm text-muted-foreground">
              Marks this browser's analytics identifier as test traffic for 24 hours (checked on the server), so QA
              visits stay out of reporting even after you sign out.
            </p>
            <Button variant="outline" onClick={markQa}>Mark this browser as QA traffic</Button>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Privacy;
